// components/operations/taxi/TaxiTeamIntegratedForm.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton,
    InputAdornment, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import {
    Badge as BadgeIcon, Business as BusinessIcon, DirectionsCar as DirectionsCarIcon,
    GroupAdd as GroupAddIcon, Lock as LockIcon, Person as PersonIcon,
    Phone as PhoneIcon, Visibility, VisibilityOff,
} from "@mui/icons-material";
import { useCreateIntegratedTaxiTeam } from "../../../hooks/taxi/useTaxiManifest";

const currentYear = new Date().getFullYear();
const roleId = (role) => Number(role?.rolid ?? role?.RolID);
const roleName = (role) => role?.rolnom ?? role?.RolNom ?? "";
const orgId = (org) => Number(org?.orgid ?? org?.OrgID);
const orgName = (org) => org?.orgnom ?? org?.OrgNom ?? "Organización";

const optionalText = (max) =>
    yup.string().trim().nullable().transform((value) => value === "" ? null : value).max(max);

const schema = yup.object({
    persona: yup.object({
        pernom: yup.string().trim().required("Los nombres son obligatorios").max(100),
        perape: yup.string().trim().required("Los apellidos son obligatorios").max(100),
        perdni: yup.string().trim().matches(/^\d{8}$/, "El DNI debe tener 8 dígitos").required(),
        pertel: yup.string().trim().nullable().transform((v) => v === "" ? null : v)
            .matches(/^(\+51)?\d{9}$/, { message: "Ingrese 9 dígitos o +51 seguido de 9 dígitos", excludeEmptyString: true }),
        perema: yup.string().trim().nullable().transform((v) => v === "" ? null : v).email("Correo inválido"),
        perdir: optionalText(255),
    }),
    empleado: yup.object({
        //rolid: yup.number().transform((v, o) => o === "" ? undefined : v).typeError("Seleccione un rol").integer().positive().required(),
        nomusu: yup.string().trim().required("El usuario es obligatorio").max(32),
        pas: yup.string().required("La contraseña es obligatoria").min(8, "Mínimo 8 caracteres"),
        //empcargo: yup.string().trim().required("El cargo es obligatorio").max(100),
        //isinv: yup.boolean().default(false),
    }),
    vehiculo: yup.object({
        orgid: yup.number().transform((v, o) => o === "" ? undefined : v).typeError("Seleccione una empresa").integer().positive().required(),
        vehmat: yup.string().trim().required("La matrícula es obligatoria").max(20),
        vehdes: optionalText(255),
        vehmar: yup.string().trim().required("La marca es obligatoria").max(100),
        vehmod: yup.string().trim().required("El modelo es obligatorio").max(100),
        vehani: yup.number().transform((v, o) => o === "" ? null : v).nullable().typeError("Año inválido").integer().min(1900).max(currentYear + 1),
        vehcap: yup.number().transform((v, o) => o === "" ? null : v).nullable().typeError("Capacidad inválida").integer().positive().max(200),
        vehest: yup.string().oneOf(["ACTIVO", "INACTIVO"]).required(),
    }),
    equipo: yup.object({
        serProId: yup.number().integer().positive().required(),
        nombre: yup.string().trim().required("El nombre del equipo es obligatorio").max(150),
        rolEmpleado: yup.string().trim().required().max(100),
        rolVehiculo: yup.string().trim().required().max(100),
    }),
});

const TaxiTeamIntegratedForm = ({ open, onClose, onSuccess, serProId, serviceName, organizations = [], }) => {
    const [showPassword, setShowPassword] = useState(false);
    const createMutation = useCreateIntegratedTaxiTeam();

    /*const conductorRole = useMemo(
        () => roles.find((role) => roleName(role).trim().toUpperCase() === "CONDUCTOR") ?? null,
        [roles]
    );*/

    const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {},
    });

    useEffect(() => {
        if (!open) return;

        reset({
            persona: { pernom: "", perape: "", perdni: "", pertel: "", perema: "", perdir: "" },
            /*empleado: {
                rolid: conductorRole ? String(roleId(conductorRole)) : "",
                nomusu: "", pas: "", empcargo: "CONDUCTOR", isinv: false,
            },*/
            vehiculo: {
                orgid: organizations[0] ? String(orgId(organizations[0])) : "",
                vehmat: "", vehdes: "", vehmar: "", vehmod: "", vehani: "", vehcap: "", vehest: "ACTIVO",
            },
            equipo: { serProId: Number(serProId) || "", nombre: "", rolEmpleado: "CONDUCTOR", rolVehiculo: "TAXI" },
        });
        setShowPassword(false);
    }, [open, reset, serProId, organizations]);

    const close = () => {
        if (createMutation.isPending) return;
        reset();
        onClose();
    };

    const submit = async (data) => {
        const phone = data.persona.pertel
            ? (data.persona.pertel.startsWith("+51") ? data.persona.pertel : `+51${data.persona.pertel}`)
            : null;

        const payload = {
            persona: {
                pernom: data.persona.pernom.trim().toUpperCase(),
                perape: data.persona.perape.trim().toUpperCase(),
                perdni: data.persona.perdni.trim(),
                pertel: phone,
                perema: data.persona.perema?.trim().toLowerCase() || null,
                perdir: data.persona.perdir?.trim() || null,
            },
            empleado: {
                //rolid: Number(data.empleado.rolid),
                nomusu: data.empleado.nomusu.trim(),
                pas: data.empleado.pas,
                //empcargo: data.empleado.empcargo.trim().toUpperCase(),
                estreg: "A",
                //isinv: Boolean(data.empleado.isinv),
                serviciosProyecto: [{
                    serProId: Number(data.equipo.serProId),
                    rolSerPro: data.equipo.rolEmpleado.trim().toUpperCase(),
                }],
            },
            vehiculo: {
                orgid: Number(data.vehiculo.orgid),
                vehmat: data.vehiculo.vehmat.trim().toUpperCase(),
                vehdes: data.vehiculo.vehdes?.trim() || null,
                vehmar: data.vehiculo.vehmar.trim().toUpperCase(),
                vehmod: data.vehiculo.vehmod.trim().toUpperCase(),
                vehani: data.vehiculo.vehani === "" || data.vehiculo.vehani === null ? null : Number(data.vehiculo.vehani),
                vehcap: data.vehiculo.vehcap === "" || data.vehiculo.vehcap === null ? null : Number(data.vehiculo.vehcap),
                vehest: data.vehiculo.vehest,
                serviciosProyecto: [{
                    serProId: Number(data.equipo.serProId),
                    rolVehPro: data.equipo.rolVehiculo.trim().toUpperCase(),
                }],
            },
            equipo: {
                serProId: Number(data.equipo.serProId),
                nombre: data.equipo.nombre.trim(),
                estreg: "A",
            },
        };

        try {
            const result = await createMutation.mutateAsync(payload);
            onSuccess?.(result, "Persona, usuario, vehículo y equipo creados correctamente.");
            reset();
            onClose();
        } catch (_) { }
    };

    const apiError = createMutation.error?.response?.data?.error
        || createMutation.error?.response?.data?.message
        || createMutation.error?.message;

    const errorText = (section, field) => errors?.[section]?.[field]?.message;

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="lg" disableEscapeKeyDown={createMutation.isPending}>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <GroupAddIcon color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">Crear equipo de taxi</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {serviceName || `Servicio-proyecto ${serProId}`}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>

            <form onSubmit={handleSubmit(submit)} noValidate>
                <DialogContent dividers>
                    <Stack spacing={3}>
                        {apiError && <Alert severity="error">{apiError}</Alert>}
                        <Alert severity="info">
                            El backend debe crear los cuatro registros en una sola transacción. Si uno falla, no debe persistirse ninguno.
                        </Alert>

                        <Box>
                            <Typography variant="h6" display="flex" gap={1}><PersonIcon color="primary" />Persona</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}><TextField label="Nombres" fullWidth {...register("persona.pernom")} error={!!errorText("persona", "pernom")} helperText={errorText("persona", "pernom")} /></Grid>
                                <Grid item xs={12} md={6}><TextField label="Apellidos" fullWidth {...register("persona.perape")} error={!!errorText("persona", "perape")} helperText={errorText("persona", "perape")} /></Grid>
                                <Grid item xs={12} md={4}><TextField label="DNI" fullWidth inputProps={{ maxLength: 8 }} {...register("persona.perdni")} error={!!errorText("persona", "perdni")} helperText={errorText("persona", "perdni")} InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon /></InputAdornment> }} /></Grid>
                                <Grid item xs={12} md={4}><TextField label="Teléfono" fullWidth placeholder="999999999" {...register("persona.pertel")} error={!!errorText("persona", "pertel")} helperText={errorText("persona", "pertel")} InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }} /></Grid>
                                <Grid item xs={12} md={4}><TextField label="Correo" type="email" fullWidth {...register("persona.perema")} error={!!errorText("persona", "perema")} helperText={errorText("persona", "perema")} /></Grid>
                                <Grid item xs={12}><TextField label="Dirección" fullWidth {...register("persona.perdir")} error={!!errorText("persona", "perdir")} helperText={errorText("persona", "perdir")} /></Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography variant="h6" display="flex" gap={1}><LockIcon color="primary" />Usuario y empleado</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                                {/*<Grid item xs={12} md={3}>
                                    <Controller name="empleado.rolid" control={control} render={({ field }) => (
                                        <TextField {...field} select label="Rol" fullWidth error={!!errorText("empleado", "rolid")} helperText={errorText("empleado", "rolid")}>
                                            <MenuItem value="">Seleccione</MenuItem>
                                            {roles.map((role) => <MenuItem key={roleId(role)} value={String(roleId(role))}>{roleName(role)}</MenuItem>)}
                                        </TextField>
                                    )} />
                                </Grid>*/}
                                {/*<Grid item xs={12} md={3}><TextField label="Cargo" fullWidth {...register("empleado.empcargo")} error={!!errorText("empleado", "empcargo")} helperText={errorText("empleado", "empcargo")} /></Grid>*/}
                                <Grid item xs={12} md={6}><TextField label="Usuario" fullWidth {...register("empleado.nomusu")} error={!!errorText("empleado", "nomusu")} helperText={errorText("empleado", "nomusu")} /></Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Contraseña" type={showPassword ? "text" : "password"} fullWidth {...register("empleado.pas")} error={!!errorText("empleado", "pas")} helperText={errorText("empleado", "pas")} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton type="button" onClick={() => setShowPassword((v) => !v)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                                </Grid>
                                {/*<Grid item xs={12}>
                                    <Controller name="empleado.isinv" control={control} render={({ field }) => <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Usuario invitado" />} />
                                </Grid>*/}
                            </Grid>
                        </Box>

                        <Box>
                            <Typography variant="h6" display="flex" gap={1}><DirectionsCarIcon color="primary" />Vehículo</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Controller name="vehiculo.orgid" control={control} render={({ field }) => (
                                        <TextField {...field} select label="Empresa proveedora" fullWidth error={!!errorText("vehiculo", "orgid")} helperText={errorText("vehiculo", "orgid")} InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon /></InputAdornment> }}>
                                            <MenuItem value="">Seleccione</MenuItem>
                                            {organizations.map((org) => <MenuItem key={orgId(org)} value={String(orgId(org))}>{orgName(org)}</MenuItem>)}
                                        </TextField>
                                    )} />
                                </Grid>
                                <Grid item xs={12} md={4}><TextField label="Matrícula" fullWidth {...register("vehiculo.vehmat")} error={!!errorText("vehiculo", "vehmat")} helperText={errorText("vehiculo", "vehmat")} /></Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller name="vehiculo.vehest" control={control} render={({ field }) => <TextField {...field} select label="Estado operativo" fullWidth disabled><MenuItem value="ACTIVO">ACTIVO</MenuItem><MenuItem value="INACTIVO">INACTIVO</MenuItem></TextField>} />
                                </Grid>
                                <Grid item xs={12} md={4}><TextField label="Marca" fullWidth {...register("vehiculo.vehmar")} error={!!errorText("vehiculo", "vehmar")} helperText={errorText("vehiculo", "vehmar")} /></Grid>
                                <Grid item xs={12} md={4}><TextField label="Modelo" fullWidth {...register("vehiculo.vehmod")} error={!!errorText("vehiculo", "vehmod")} helperText={errorText("vehiculo", "vehmod")} /></Grid>
                                <Grid item xs={12} md={2}><TextField label="Año" type="number" fullWidth {...register("vehiculo.vehani")} error={!!errorText("vehiculo", "vehani")} helperText={errorText("vehiculo", "vehani")} /></Grid>
                                <Grid item xs={12} md={2}><TextField label="Capacidad" type="number" fullWidth {...register("vehiculo.vehcap")} error={!!errorText("vehiculo", "vehcap")} helperText={errorText("vehiculo", "vehcap")} /></Grid>
                                <Grid item xs={12}><TextField label="Descripción" multiline minRows={2} fullWidth {...register("vehiculo.vehdes")} error={!!errorText("vehiculo", "vehdes")} helperText={errorText("vehiculo", "vehdes")} /></Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography variant="h6" display="flex" gap={1}><GroupAddIcon color="primary" />Equipo operativo</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}><TextField label="Servicio-proyecto" fullWidth value={serviceName || serProId || ""} disabled /></Grid>
                                <Grid item xs={12} md={4}><TextField label="Nombre del equipo" placeholder="Ej. Equipo Taxi 01" fullWidth {...register("equipo.nombre")} error={!!errorText("equipo", "nombre")} helperText={errorText("equipo", "nombre")} /></Grid>
                                <Grid item xs={12} md={2}><TextField label="Función conductor" fullWidth {...register("equipo.rolEmpleado")} error={!!errorText("equipo", "rolEmpleado")} helperText={errorText("equipo", "rolEmpleado")} disabled/></Grid>
                                <Grid item xs={12} md={2}><TextField label="Función vehículo" fullWidth {...register("equipo.rolVehiculo")} error={!!errorText("equipo", "rolVehiculo")} helperText={errorText("equipo", "rolVehiculo")} disabled/></Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={close} disabled={createMutation.isPending}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={createMutation.isPending} startIcon={createMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <GroupAddIcon />}>
                        {createMutation.isPending ? "Creando..." : "Crear equipo completo"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TaxiTeamIntegratedForm;